// includes/js-src/log/containers.ts
//
// Client-side renderers for the four grouped CONTAINER wrappers that
// initial-load buildLog needs:
//   - contesting rollup (mass-contest)
//   - death-resolution session
//   - trade-session group
//   - gate-failure rollup
//
// Plus renderLogWindowFromJson() which is the JSON-mode dispatcher driven
// by buildLog() in side-panels-init.js.

import type { Roll, CollapseLabelTask, RenderedLogWindow, RenderLogWindowInput } from './types';
import {
    renderSimpleLogRow,
    renderLogTimestamp,
    renderHouseRuleBanner,
    renderLogLineThumbnail,
    normalizePicUrl,
} from './log-render';

type ItemArr = Record<string, unknown>;
function _first(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : undefined;
}

// ===================================================================
// Helpers
// ===================================================================

function _esc(s: unknown): string {
    if (typeof escapeHtml === 'function') return escapeHtml(String(s ?? ''));
    const map: Record<string, string> = {
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}

function _icon(key: unknown, w?: number, h?: number): string {
    if (typeof iconMap !== 'function') return '';
    const k = String(key ?? '');
    if (w == null && h == null) return iconMap(k);
    return iconMap(k, w || 25, h || w || 25);
}

function _isUuid(s: unknown): boolean {
    if (typeof checkIfValidUUID === 'function') return checkIfValidUUID(String(s));
    return typeof s === 'string' && /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(s);
}

function _ucwords(s: unknown): string {
    if (typeof ucwordsAndSpace === 'function') return ucwordsAndSpace(String(s ?? ''));
    return String(s || '').replace(/_/g, ' ').split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function _isGm(): boolean {
    return !!(window.FateTide && window.FateTide.user && window.FateTide.user.isGM);
}

let _CONTESTING_ROLL_TYPE_ID: string | null = null;
function _contestingRollTypeId() {
    if (_CONTESTING_ROLL_TYPE_ID !== null) return _CONTESTING_ROLL_TYPE_ID;
    const cat = (typeof ROLLTYPE_CATALOG !== 'undefined') ? ROLLTYPE_CATALOG : null;
    _CONTESTING_ROLL_TYPE_ID = (cat && cat.contesting_roll && cat.contesting_roll.item_id)
        ? String(cat.contesting_roll.item_id)
        : '';
    return _CONTESTING_ROLL_TYPE_ID;
}

function _buildContestantArr(rollRow: Roll): ItemArr {
    const r = (rollRow || {}) as Roll;
    return {
        item_id:                    [String(r.roll_by || '')],
        item_name:                  [String(r.roll_by_item_name || '')],
        item_type:                  [String(r.roll_by_item_type || '')],
        creature_number:            [r.roll_by_creature_number ?? 0],
        encounter_creature_private: [r.roll_by_encounter_creature_private ?? 0],
        pic_url:                    [normalizePicUrl(r.roll_by_pic_url)],
    };
}

function _hexOnly(uuid: unknown): string {
    return String(uuid || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase();
}

interface ChevronOpts {
    collapseId: string;
    labelClass: string;
    wrapClass?: string;
    hidden?: boolean;
    count?: number | null;
    labelText?: string;
}

function _chevronToggle(opts: ChevronOpts): string {
    const collapseId = String(opts.collapseId || '');
    const labelClass = String(opts.labelClass || '');
    const wrapClass = opts.wrapClass ? ` ${_esc(opts.wrapClass)}` : '';
    const styleAttr = opts.hidden ? ' style="display:none;"' : '';
    const countAttr = (opts.count !== undefined && opts.count !== null)
        ? ` data-ft-group-count="${opts.count}"` : '';
    const labelText = String(opts.labelText
        || (opts.count !== undefined && opts.count !== null ? `Show details (${opts.count})` : 'Show details'));
    return `<button type="button"
        class="ft-edge-chevron-toggle${wrapClass}"
        data-bs-toggle="collapse"
        data-bs-target="#${_esc(collapseId)}"
        aria-expanded="false"
        aria-controls="${_esc(collapseId)}"
        aria-label="${_esc(labelText)}"${countAttr}${styleAttr}>
        <span class="${_esc(labelClass)} visually-hidden">${_esc(labelText)}</span>
    </button>`;
}

function _activateCollapseLabel(collapseId: string, labelSelector: string, labelHtmlFor: (showing: boolean) => string): void {
    const el = document.getElementById(collapseId);
    if (!el || typeof bootstrap === 'undefined' || !bootstrap.Collapse) return;
    const wrap = el.closest('.mass-contest-rollup, .death-log-session-group, .trade-log-trade-group, .ft-log-gate-failure-rollup');
    const btn = wrap ? wrap.querySelector(`button[data-bs-toggle="collapse"][data-bs-target="#${CSS.escape(el.id)}"]`) : null;
    const label = btn ? btn.querySelector(labelSelector) : null;
    if (!label) return;
    el.addEventListener('show.bs.collapse', () => { label.textContent = labelHtmlFor(true); });
    el.addEventListener('hide.bs.collapse', () => { label.textContent = labelHtmlFor(false); });
}

// ===================================================================
// renderContestingSummary
// ===================================================================

function renderContestingSummaryBody(peersForRoll: Roll[], campaignPermission: number): string {
    if (!Array.isArray(peersForRoll) || peersForRoll.length < 2) return '';

    const sorted = peersForRoll.slice().sort((a, b) =>
        (a.roll_i ?? 999) - (b.roll_i ?? 999)
    );
    const contestor_roll = sorted[0];

    const viewerCanSeePrivate = (roll: Roll): boolean =>
        campaignPermission === 1 || Number(roll.campaign_permission || 0) === 1;
    const concealedForViewer = (roll: Roll): boolean =>
        Number(roll.roll_private || 0) > 0 && !viewerCanSeePrivate(roll);
    const hasConcealedSide = sorted.some(concealedForViewer);

    const defenders = sorted.slice(1).sort((a, b) => {
        // Do not use a concealed result to choose or order the visible defender.
        if (hasConcealedSide) return (a.roll_i ?? 999) - (b.roll_i ?? 999);
        const cmp = (Number(b.roll_result || 0)) - (Number(a.roll_result || 0));
        return cmp !== 0 ? cmp : ((a.roll_i ?? 999) - (b.roll_i ?? 999));
    });
    const contestee_roll = defenders[0];
    if (!contestor_roll || !contestee_roll) return '';

    const contestor = {
        roll_result:   Number(contestor_roll.roll_result || 0),
        skill_used:    String(contestor_roll.roll_type_2 || ''),
        roll_private:  Number(contestor_roll.roll_private || 0),
    };
    const contestee = {
        roll_result:   Number(contestee_roll.roll_result || 0),
        skill_used:    String(contestee_roll.roll_type_2 || ''),
        roll_private:  Number(contestee_roll.roll_private || 0),
    };

    if ((contestor.roll_private === 2 && !viewerCanSeePrivate(contestor_roll))
        || (contestee.roll_private === 2 && !viewerCanSeePrivate(contestee_roll))) {
        return '';
    }

    const difference = contestor.roll_result - contestee.roll_result;
    const contestor_wins = difference >= 0;
    const contestorConcealed = concealedForViewer(contestor_roll);
    const contesteeConcealed = concealedForViewer(contestee_roll);
    const outcomeConcealed = contestorConcealed || contesteeConcealed;

    const contestorArr = _buildContestantArr(contestor_roll);
    const contesteeArr = _buildContestantArr(contestee_roll);

    function resultBadge(
        side: { roll_result: number; skill_used: string; roll_private: number },
        row: Roll,
        bgClass: string,
    ): string {
        if (side.roll_private === 0) {
            return `<span class="badge ${bgClass} border border-dark">${_esc(side.roll_result)}</span>`;
        }
        if (viewerCanSeePrivate(row)) {
            const privacyIcon = side.roll_private === 2 ? 'secret_roll' : 'hidden_roll';
            return `<span class="badge ${bgClass} border border-dark">${_icon(privacyIcon)}${_esc(side.roll_result)}</span>`;
        }
        return `<span class="badge ${bgClass} border border-dark">${_icon('hidden_roll')}</span>`;
    }

    let diffBadge;
    if (outcomeConcealed) {
        diffBadge = `<span class="badge bg-secondary border border-dark">${_icon('hidden_roll')}</span>`;
    } else if (contestor.skill_used === 'ability_dc') {
        const ok = difference < 0;
        const cls = ok ? 'bg-success' : 'bg-danger';
        diffBadge = `<span class="badge ${cls} border border-dark">${ok ? 'Success' : 'Fail'}</span>`;
    } else {
        const cls = contestor_wins ? 'bg-success' : 'bg-danger';
        const glow = (Math.abs(difference) > 5)
            ? (contestor_wins
                ? 'box-shadow:0 0 8px rgba(25,135,84,0.85);'
                : 'box-shadow:0 0 8px rgba(220,53,69,0.85);')
            : '';
        const styleAttr = glow ? ` style="${glow}"` : '';
        diffBadge = `<span class="badge ${cls} border border-dark"${styleAttr}>${_esc(difference)}</span>`;
    }

    return `<div class="row p-1 border rounded">
        <div class="d-flex justify-content-center align-items-center p-1">
            <div class="col-5 p-1 rounded ${!outcomeConcealed && contestor_wins ? 'bg-success' : ''}">
                <div class="d-flex justify-content-center">${renderLogLineThumbnail(contestorArr, { thumbnail_link: 1, width: 40, height: 40 })}</div>
                <div class="text-center mt-1">${_icon(contestor.skill_used)} ${resultBadge(contestor, contestor_roll, 'bg-primary text-dark')}</div>
            </div>
            <div class="col-2 d-flex flex-column justify-content-center align-items-center text-center p-1">
                <span role="button"
                      style="line-height:1;"
                      onclick="openItemInfo('AD80D31D-898B-66CE-0894-1335D11B1529');">
                    ${_icon('contesting_roll')}
                </span>
                <div class="my-1">VS</div>
                ${diffBadge}
            </div>
            <div class="col-5 p-1 rounded ${!outcomeConcealed && !contestor_wins ? 'bg-success' : ''}">
                <div class="d-flex justify-content-center">${renderLogLineThumbnail(contesteeArr, { thumbnail_link: 1, width: 40, height: 40 })}</div>
                <div class="text-center mt-1">${_icon(contestee.skill_used)} ${resultBadge(contestee, contestee_roll, 'bg-secondary')}</div>
            </div>
        </div>
    </div>`;
}

function renderContestingSummary(peersForRoll: Roll[], campaignPermission: number): string {
    // Filter to viewer-visible peers before building the summary — the same guard the initial
    // renderer applies inline (renderContestingSummaryBody only checks roll_private===2, not a
    // hidden-creature caster). This is an exported/window-global entry point, so harden it here
    // too in case a future caller uses it directly.
    const visiblePeers = (peersForRoll || []).filter(p => _ftRollVisibleToViewer(p, campaignPermission));
    const body = renderContestingSummaryBody(visiblePeers, campaignPermission);
    if (!body) return '';
    return `<li class="list-group-item mb-0 border-bottom-0">${body}</li>`;
}

// ===================================================================
// Pre-scan helpers
// ===================================================================

const _DEATH_LOG_ANCHOR_TYPES = new Set([
    'creature_killed', 'creature_killed_forever', 'creature_destroyed',
    'creature_body_vanishes',
    'creature_transform', 'dies',
]);

function _ftDeathLogDetectSessions(rolls: Roll[]): Map<string, number> {
    const rowCountByRid = new Map<string, number>();
    for (const r of rolls) {
        const rid = String(r.roll_id || '');
        if (rid === '') continue;
        rowCountByRid.set(rid, (rowCountByRid.get(rid) || 0) + 1);
    }
    const sessions = new Map<string, number>();
    for (const r of rolls) {
        const rid = String(r.roll_id || '');
        const type = String(r.roll_type_1 || '');
        if (rid === '' || !_DEATH_LOG_ANCHOR_TYPES.has(type)) continue;
        const ri = Number(r.roll_i || 0);
        const cur = sessions.get(rid);
        if (cur === undefined || ri > cur) sessions.set(rid, ri);
    }
    for (const rid of Array.from(sessions.keys())) {
        if ((rowCountByRid.get(rid) || 0) < 2) sessions.delete(rid);
    }
    return sessions;
}

function _ftDeathLogIsPrimaryRow(roll: Roll, sessions: Map<string, number>): boolean {
    const rid = String(roll.roll_id || '');
    if (!sessions.has(rid)) return false;
    return Number(roll.roll_i || 0) === sessions.get(rid)
        && _DEATH_LOG_ANCHOR_TYPES.has(String(roll.roll_type_1 || ''));
}

function _ftDeathLogShouldSkipRow(roll: Roll, sessions: Map<string, number>): boolean {
    if (String(roll.roll_type_1 || '') === 'massive_damage_instant_death') return false;
    const rid = String(roll.roll_id || '');
    if (!sessions.has(rid)) return false;
    const pri = sessions.get(rid);
    if (Number(roll.roll_i || 0) !== pri) return true;
    return !_DEATH_LOG_ANCHOR_TYPES.has(String(roll.roll_type_1 || ''));
}

// A row is hidden from a non-GM viewer when it's a secret roll (roll_private===2) or its
// caster is a hidden creature — EXCEPT a lair, whose token is private but whose actions are
// always shown to the party. This is the single source of truth for that predicate; the main
// loop's visibleIndicesByRid and the gate-failure collector call it, and so must every path
// that pulls rows OUTSIDE the main loop (the death/trade detail collectors) — otherwise a
// private detail row bypasses the main-loop privacy check and leaks its body into a collapse.
function _ftRollVisibleToViewer(r: Roll, campaignPermission: number): boolean {
    if (campaignPermission === 1 || Number(r.campaign_permission || 0) === 1) return true;
    const casterIsLair = String(r.roll_by_item_type || '').toLowerCase() === 'lair';
    if (Number(r.roll_private || 0) === 2) return false;
    if (Number(r.roll_by_encounter_creature_private || 0) === 1 && !casterIsLair) return false;
    return true;
}

function _ftDeathLogCollectDetailRows(rolls: Roll[], rollId: string, primaryRi: number, campaignPermission: number): Roll[] {
    const out: Roll[] = [];
    for (const r of rolls) {
        if (String(r.roll_id || '') !== rollId) continue;
        if (Number(r.roll_i || 0) === primaryRi) continue;
        if (String(r.roll_type_1 || '') === 'massive_damage_instant_death') continue;
        if (!_ftRollVisibleToViewer(r, campaignPermission)) continue;
        out.push(r);
    }
    out.sort((a, b) => Number(b.roll_i || 0) - Number(a.roll_i || 0));
    return out;
}

function _ftTradeLogCollectRows(rolls: Roll[], rollId: string, campaignPermission: number): Roll[] {
    const out: Roll[] = [];
    for (const r of rolls) {
        if (String(r.roll_id || '') !== rollId) continue;
        if (String(r.roll_type_1 || '') !== 'trade_inventory') continue;
        if (!_ftRollVisibleToViewer(r, campaignPermission)) continue;
        out.push(r);
    }
    out.sort((a, b) => Number(a.roll_i || 0) - Number(b.roll_i || 0));
    return out;
}

function _ftContestPeersByRid(rolls: Roll[]): Map<string, Roll[]> {
    const contestId = _contestingRollTypeId();
    const map = new Map<string, Roll[]>();
    if (contestId === '') return map;
    for (const r of rolls) {
        if (String(r.roll_type_1 || '') !== contestId) continue;
        const rid = String(r.roll_id || '');
        if (rid === '') continue;
        if (!map.has(rid)) map.set(rid, []);
        map.get(rid)!.push(r);
    }
    return map;
}

interface GateFailureContext {
    gatesByRid: Map<string, Roll[]>;
    anchorIndexByRid: Map<string, number>;
    rollupEnabled: Set<string>;
}
function _ftGateFailureCollect(rolls: Roll[], campaignPermission: number): GateFailureContext {
    const FT = 'post_effect_condition_failed';
    const gatesByRid = new Map<string, Roll[]>();
    const anchorIndexByRid = new Map<string, number>();
    rolls.forEach((r, idx) => {
        if (!_ftRollVisibleToViewer(r, campaignPermission)) return;
        const rid = String(r.roll_id || '');
        if (rid === '') return;
        if (String(r.roll_type_1 || '') === FT) {
            if (!gatesByRid.has(rid)) gatesByRid.set(rid, []);
            gatesByRid.get(rid)!.push(r);
        } else {
            if (!anchorIndexByRid.has(rid)) anchorIndexByRid.set(rid, idx);
        }
    });
    const rollupEnabled = new Set<string>();
    for (const [rid, rows] of gatesByRid) {
        if (rows.length === 0 || !anchorIndexByRid.has(rid)) continue;
        rollupEnabled.add(rid);
    }
    return { gatesByRid, anchorIndexByRid, rollupEnabled };
}

// ===================================================================
// Death-session
// ===================================================================

const _DEATH_SUMMARY_LABEL: Record<string, string> = {
    creature_killed:         'is killed.',
    creature_killed_forever: 'is killed forever!',
    creature_destroyed:      'is destroyed!',
    creature_body_vanishes:  'body vanishes!',
    creature_transform:      'Transforms!',
    dies:                    'Dies...',
};

interface DeathSessionBodyResult {
    collapseId: string | null;
    count: number;
    html: string;
}
function renderDeathSessionBody(anchorRoll: Roll, detailRolls: Roll[], opts?: { hideToggleWhenEmpty?: boolean }): DeathSessionBodyResult {
    opts = opts || {};
    const t = String(anchorRoll.roll_type_1 || 'creature_killed');
    const label = _DEATH_SUMMARY_LABEL[t] || 'is killed.';
    const iconKey = (t === 'creature_transform') ? 'transforms'
        : (t === 'creature_body_vanishes') ? 'body_vanishes_at_zero_vitality'
        : t;
    const rollIdRaw = String(anchorRoll.roll_id || '');
    const collapseId = 'dlc_' + (_hexOnly(rollIdRaw) || Math.random().toString(16).slice(2, 18));
    const n = detailRolls.length;
    const hideToggleWhenEmpty = !!opts.hideToggleWhenEmpty;

    let transformThumb = '';
    if (t === 'creature_transform') {
        const pic = normalizePicUrl(anchorRoll.roll_to_pic_url);
        if (pic) {
            transformThumb = `<div class="text-center mb-2 mt-2 death-log-transform-into-thumb">
                <img src="${_esc(pic)}" class="img-thumbnail mx-auto d-block" alt="" style="max-height: 120px; width: auto;"/>
            </div>`;
        }
    }

    let detailHtml = '';
    if (n > 0 || hideToggleWhenEmpty) {
        const inner = detailRolls.map((dr: Roll) => {
            const wrapped = renderSimpleLogRow(dr, { inline: true });
            return `<div class="death-log-detail-roll mb-1">${wrapped}</div>`;
        }).join('');
        detailHtml = `${_chevronToggle({
            collapseId,
            count: n,
            labelClass: 'death-log-toggle-label',
            wrapClass: 'death-log-toggle-wrap',
            hidden: (n === 0 && hideToggleWhenEmpty),
        })}
        <div id="${_esc(collapseId)}" class="collapse death-log-detail">${inner}</div>`;
    }

    return {
        collapseId: (n > 0 || hideToggleWhenEmpty) ? collapseId : null,
        count: n,
        html: `<div class="text-left death-log-session-group">
            <div class="row m-0">
                <div class="col-12 text-left">${_icon(iconKey)} ${_esc(label)}</div>
            </div>
            ${transformThumb}
            ${detailHtml}
        </div>`,
    };
}

// ===================================================================
// Trade-session
// ===================================================================

interface TradeSessionBodyResult {
    collapseId: string | null;
    count: number;
    html: string;
}
function renderTradeSessionBody(rows: Roll[]): TradeSessionBodyResult {
    if (!Array.isArray(rows) || rows.length === 0) return { collapseId: null, count: 0, html: '' };

    const first = rows[0];
    if (!first) return { collapseId: null, count: 0, html: '' };
    const rollIdRaw = String(first.roll_id || '');
    const collapseId = 'tlc_' + (_hexOnly(rollIdRaw) || Math.random().toString(16).slice(2, 18));
    const n = rows.length;

    const nameFrom = String(first.roll_by_item_name || '') || 'Someone';
    const nameTo = String(first.roll_to_item_name || '') || 'Someone';

    const playerThumb = (first.roll_by_arr && typeof first.roll_by_arr === 'object') ? first.roll_by_arr : null;
    const merchantThumb = (first.roll_to_arr && typeof first.roll_to_arr === 'object') ? first.roll_to_arr : null;

    const headerThumbs = `<div class="d-flex justify-content-center align-items-center gap-3 mb-3">
        ${playerThumb ? renderLogLineThumbnail(playerThumb, { width: 56, height: 56, thumbnail_link: 1 }) : ''}
        <span class="d-inline-flex">${_icon('trade_inventory')}</span>
        ${merchantThumb ? renderLogLineThumbnail(merchantThumb, { width: 56, height: 56, thumbnail_link: 1 }) : ''}
    </div>`;

    const rowsHtml = rows.map(sub => {
        let qty = parseInt(String(sub.roll_result || 1), 10);
        if (qty < 1) qty = 1;
        const qtyBadge = qty !== 1 ? `<span class="badge bg-dark border">${qty}</span>` : '';
        const itemId = String(sub.roll_item_id || '');
        const itemName = String(sub.roll_item_id_item_name || '');
        const itemThumb = (sub.roll_item_arr && typeof sub.roll_item_arr === 'object') ? sub.roll_item_arr : null;
        const toThumb   = (sub.roll_to_arr   && typeof sub.roll_to_arr   === 'object') ? sub.roll_to_arr   : null;
        return `<div class="border rounded p-2 mb-2 bg-body-secondary bg-opacity-25">
            <div class="small border-bottom pb-1 mb-2">
                ${_icon('trade_inventory')}
                ${qtyBadge}
                <span role="button" class="link-primary" onclick="openItemInfo('${_esc(itemId)}');">${_esc(itemName)}</span>
            </div>
            <div class="d-flex align-items-center justify-content-center flex-wrap gap-1">
                ${itemThumb ? renderLogLineThumbnail(itemThumb, { width: 52, height: 52, thumbnail_link: 1 }) : ''}
                <div class="px-1">${_icon('trade_inventory')}</div>
                ${toThumb   ? renderLogLineThumbnail(toThumb,   { width: 52, height: 52, thumbnail_link: 1 }) : ''}
            </div>
        </div>`;
    }).join('');

    const detailBlock = n > 0 ? `${_chevronToggle({
        collapseId,
        labelClass: 'trade-log-trade-toggle-label',
        wrapClass: 'trade-log-trade-toggle-wrap',
        labelText: 'Show details',
    })}
    <div id="${_esc(collapseId)}" class="collapse trade-log-trade-detail">${rowsHtml}</div>` : '';

    return {
        collapseId: n > 0 ? collapseId : null,
        count: n,
        html: `<div class="text-left trade-log-trade-group">
            <span class="visually-hidden">${_esc(nameFrom)} traded with ${_esc(nameTo)}</span>
            ${headerThumbs}
            ${detailBlock}
        </div>`,
    };
}

// ===================================================================
// Gate-failure rollup
// ===================================================================

const _GATE_REASON_HUMAN_MAP: Record<string, string> = {
    ability_only_works_against_size_category_number_below: 'Size gate: target is too large relative to required size tier for this effect.',
    ability_only_works_against_size_category_number_above: 'Size gate: target is too small relative to required size tier for this effect.',
    works_only_against_creature_type: 'Creature type gate: rider only applies versus the matching creature type; this target does not qualify.',
    does_not_work_against_creature_type: 'Creature type gate: this rider does not apply versus this creature type.',
    works_only_against_creature_sub_type: 'Creature sub-type gate: rider only applies versus the matching sub-type; this target does not qualify.',
    does_not_work_against_creature_sub_type: 'Creature sub-type gate: this rider does not apply versus this creature sub-type.',
    works_only_against_creature_lineage: 'Creature lineage gate: rider only applies versus the matching lineage; this target does not qualify.',
    does_not_work_against_creature_lineage: 'Creature lineage gate: this rider does not apply versus this creature lineage.',
    works_only_against_creature_ancestry: 'Creature ancestry gate: rider only applies versus the matching ancestry; this target does not qualify.',
    does_not_work_against_creature_ancestry: 'Creature ancestry gate: this rider does not apply versus this creature ancestry.',
    works_only_against_creature_grouping: 'Creature grouping gate: rider only applies versus the matching grouping; this target does not qualify.',
    does_not_work_against_creature_grouping: 'Creature grouping gate: this rider does not apply versus this creature grouping.',
    works_only_against_creature_classification: 'Creature classification gate: only applies versus the listed sub-types / lineages / ancestries / groupings; this target does not qualify.',
    target_immune_to_ability: 'Precheck gate: target is flagged immune to this ability, so chained riders did not run.',
    immune_to_spells: 'Precheck gate: target is immune to spells; spell-type rider did not apply.',
    immune_to_thought_reading: 'Precheck gate: the thoughts of this target cannot be read, so the thought-reading effect did not apply.',
    if_target_has_status_effect_active: 'Precheck gate: target does not have the required status active, so this rider did not apply.',
    if_target_has_status_effect_not_active: 'Precheck gate: target has a forbidding status active, so this rider did not apply.',
    if_caster_has_status_effect_active: 'Precheck gate: caster does not have the required status active, so this rider did not apply.',
    if_caster_has_status_effect_not_active: 'Precheck gate: caster has a forbidding status active, so this rider did not apply.',
};

const _GATE_ICON_BANNER_CAPTION: Record<string, string> = {
    if_target_has_status_effect_active: 'Target needed any of these statuses active (none were active):',
    if_target_has_status_effect_not_active: 'This rider is blocked while the target has any of these statuses:',
    if_caster_has_status_effect_active: 'Caster needed any of these statuses active (none were active):',
    if_caster_has_status_effect_not_active: 'This rider is blocked while the caster has any of these statuses:',
    works_only_against_creature_type: 'Rider applies only to these creature types:',
    does_not_work_against_creature_type: 'Creature type immune / does not apply (listed types):',
    works_only_against_creature_sub_type: 'Rider applies only to these creature sub-types:',
    does_not_work_against_creature_sub_type: 'Creature sub-type does not apply (listed sub-types):',
    works_only_against_creature_lineage: 'Rider applies only to these creature lineages:',
    does_not_work_against_creature_lineage: 'Creature lineage does not apply (listed lineages):',
    works_only_against_creature_ancestry: 'Rider applies only to these creature ancestries:',
    does_not_work_against_creature_ancestry: 'Creature ancestry does not apply (listed ancestries):',
    works_only_against_creature_grouping: 'Rider applies only to these creature groupings:',
    does_not_work_against_creature_grouping: 'Creature grouping does not apply (listed groupings):',
    works_only_against_creature_classification: 'Applies only to these creature classifications:',
    target_immune_to_ability: 'Target immune to this ability:',
    immune_to_spells: 'Spell-type rider blocked (source item):',
    immune_to_thought_reading: 'Thought-reading blocked (source item):',
};

function _gateReasonHuman(layer: string): string {
    return _GATE_REASON_HUMAN_MAP[layer] || _ucwords(layer);
}

function _gateIconBannerCaption(layer: string): string {
    return _GATE_ICON_BANNER_CAPTION[layer] || 'Related items:';
}

function _splitRollsFullLines(rollsFull: unknown): string[] {
    if (!rollsFull) return [];
    return String(rollsFull).split(/\r\n|\r|\n/).map(s => s.trim()).filter(Boolean);
}

function _parseIconUuidsFromRollsFull(rollsFull: unknown): string[] {
    for (const line of _splitRollsFullLines(rollsFull)) {
        let payload: string | null = null;
        if (line.startsWith('ft_gate_icons:'))   payload = line.slice('ft_gate_icons:'.length);
        else if (line.startsWith('ft_status_gate:')) payload = line.slice('ft_status_gate:'.length);
        if (payload === null) continue;
        const out = [];
        for (const part of payload.split('|')) {
            const t = part.trim();
            if (t !== '' && _isUuid(t)) out.push(t);
        }
        return Array.from(new Set(out));
    }
    return [];
}

function _parseItemPathUuidsFromRollsFull(rollsFull: unknown): string[] {
    for (const line of _splitRollsFullLines(rollsFull)) {
        if (!line.startsWith('ft_gate_item_path:')) continue;
        const out = [];
        for (const part of line.slice('ft_gate_item_path:'.length).split('|')) {
            const t = part.trim();
            if (t !== '' && _isUuid(t)) out.push(t);
        }
        return Array.from(new Set(out));
    }
    return [];
}

function _isStructuredGateLine(line: string): boolean {
    return line.startsWith('ft_size_gate:')
        || line.startsWith('ft_gate_icons:')
        || line.startsWith('ft_status_gate:')
        || line.startsWith('ft_gate_item_path:');
}

function _residualRollsFullPlaintext(rollsFull: unknown): string {
    return _splitRollsFullLines(rollsFull).filter(l => !_isStructuredGateLine(l)).join('\n');
}

interface SizeGate { casterId: string; targetId: string; anchorId: string; below: boolean; }
function _parseSizeGate(rollsFull: unknown): SizeGate | null {
    for (const line of _splitRollsFullLines(rollsFull)) {
        if (!line.startsWith('ft_size_gate:')) continue;
        const parts = line.slice('ft_size_gate:'.length).split('|');
        if (parts.length !== 4) return null;
        const [casterId, targetId, anchorId, cmp] = parts as [string, string, string, string];
        if (!_isUuid(casterId) || !_isUuid(targetId) || !_isUuid(anchorId)) return null;
        return { casterId, targetId, anchorId, below: cmp === 'below' };
    }
    return null;
}

function _renderGateFailureDetail(gateRow: Roll, isLast: boolean): string {
    const layer = String(gateRow.roll_type_2 || '');
    const why = _gateReasonHuman(layer);
    const rollsFull = String(gateRow.rolls_full || '');
    const iconUuids = _parseIconUuidsFromRollsFull(rollsFull);
    const pathUuids = _parseItemPathUuidsFromRollsFull(rollsFull);
    const size = _parseSizeGate(rollsFull);
    const residual = _residualRollsFullPlaintext(rollsFull);

    const SZ_ROLE = 22;
    const SZ_RANK = 28;
    const SZ_STATUS = 26;

    const wrapCls = isLast ? 'pb-1' : 'border-bottom pb-2 mb-2';
    const itemId = String(gateRow.roll_item_id || '');
    const itemName = String(gateRow.roll_item_id_item_name || '');

    let pathBlock = '';
    if (pathUuids.length > 0) {
        const parts = pathUuids.map((id, i) => `${i > 0 ? '<span class="opacity-50 user-select-none" aria-hidden="true">→</span>' : ''}
            <span class="link-primary" type="button" onclick="openItemInfo('${_esc(id)}');">
                ${_icon(id, SZ_STATUS, SZ_STATUS)}
            </span>`).join('');
        pathBlock = `<div class="small mt-1 ft-log-post-effect-item-path d-flex align-items-center flex-wrap gap-1">
            <span class="text-muted">Source chain (host → use):</span>
            ${parts}
        </div>`;
    }

    let iconsBlock = '';
    if (iconUuids.length > 0) {
        const icons = iconUuids.map(id => `<span class="d-inline-flex align-items-center">${_icon(id, SZ_STATUS, SZ_STATUS)}</span>`).join('');
        iconsBlock = `<div class="small mt-2 pt-2 border-top ft-log-post-effect-gate-icons">
            <div class="text-muted">${_esc(_gateIconBannerCaption(layer))}</div>
            <div class="d-flex align-items-center flex-wrap gap-1 mt-1">${icons}</div>
        </div>`;
    }

    let sizeBlock = '';
    if (size) {
        sizeBlock = `<div class="small mt-2 pt-2 border-top ft-log-post-effect-size-icons">
            <div class="d-flex align-items-center flex-wrap gap-2">
                <span class="d-inline-flex align-items-center gap-2">${_icon('caster', SZ_ROLE, SZ_ROLE)} ${_icon(size.casterId, SZ_RANK, SZ_RANK)}</span>
                <span class="opacity-50 user-select-none" aria-hidden="true">|</span>
                <span class="d-inline-flex align-items-center gap-2">${_icon('target', SZ_ROLE, SZ_ROLE)} ${_icon(size.targetId, SZ_RANK, SZ_RANK)}</span>
            </div>
            <div class="d-inline-flex align-items-center flex-wrap gap-2 mt-2">
                ${_icon('creature_size', SZ_ROLE, SZ_ROLE)} ${_icon(size.anchorId, SZ_RANK, SZ_RANK)}
                <span>${size.below ? 'or smaller.' : 'or larger.'}</span>
            </div>
        </div>`;
    }

    let residualBlock = '';
    if (residual !== '') {
        residualBlock = `<div class="small mt-2 pt-2 border-top">${_esc(residual).replace(/\n/g, '<br>')}</div>`;
    }

    const targetThumb = renderLogLineThumbnail(gateRow.roll_to_arr || {}, { thumbnail_link: 1 });

    return `<div class="text-left ${wrapCls} ft-log-gate-detail">
        <div class="fw-bold">Gate not met</div>
        <div class="mt-2 mb-1">
            <span class="link-primary" type="button" onclick="openItemInfo('${_esc(itemId)}');">
                ${_icon(itemId)} ${_esc(itemName)}
            </span>
        </div>
        ${pathBlock}
        <div class="small">${_esc(why)}</div>
        ${iconsBlock}
        ${sizeBlock}
        ${residualBlock}
        <div class="d-flex align-items-center justify-content-center col-12 pt-2" data-ft-log-target="1">${targetThumb}</div>
    </div>`;
}

interface GateFailureRollupResult { collapseId: string | null; count: number; html: string; }
function renderGateFailureRollup(rollId: string, gateRows: Roll[]): GateFailureRollupResult {
    if (!Array.isArray(gateRows) || gateRows.length === 0) {
        return { collapseId: null, count: 0, html: '' };
    }
    const n = gateRows.length;
    const collapseId = 'ftgf_' + (_hexOnly(rollId) || Math.random().toString(16).slice(2, 18));
    const lastIdx = gateRows.length - 1;
    const detailHtml = gateRows.map((r, i) => _renderGateFailureDetail(r, i === lastIdx)).join('');
    const html = `<div class="col-12 mt-2 pt-2 border-top ft-log-gate-failure-rollup">
        <button type="button" class="btn btn-sm btn-outline-secondary border"
            data-bs-toggle="collapse" data-bs-target="#${_esc(collapseId)}"
            aria-expanded="false" aria-controls="${_esc(collapseId)}"
            data-ft-group-count="${n}">
            <span class="ft-log-gate-toggle-label">Gates / riders not met (${n})</span>
        </button>
        <div class="collapse mt-2 ft-log-gate-failure-collapse-body border rounded p-2 bg-body-secondary bg-opacity-10" id="${_esc(collapseId)}">
            ${detailHtml}
        </div>
    </div>`;
    return { collapseId, count: n, html };
}

// ===================================================================
// _wrapRow (containers internal — anchor + body + gate-rollup)
// ===================================================================

function _wrapRow(roll: Roll, bodyHtml: string, gateRollupHtml: string, opts?: { suppressTimestamp?: boolean; suppressDataRollId?: boolean }): string {
    opts = opts || {};
    const rollId = _esc(roll.roll_id || '');
    const hasCampaignId = !!roll.campaign_id;
    const colByThumb = `<div class="d-flex justify-content-center align-items-center col-3 border-end p-1">${renderLogLineThumbnail(roll.roll_by_arr || {}, { thumbnail_link: 1 })}</div>`;
    const banner = renderHouseRuleBanner(roll);
    const ts = opts.suppressTimestamp ? '' : renderLogTimestamp(roll, hasCampaignId);
    const rollIdAttr = opts.suppressDataRollId ? '' : `data-ft-roll-id="${rollId}"`;
    return `<li class="list-group-item mb-1 pb-0 pt-0 border-bottom-0" ${rollIdAttr} style="position: relative;">
        <div class="row p-1 border rounded" style="min-height:70px;">
            ${colByThumb}
            <div class="col-9 d-flex align-items-center">
                <div class="col-12 p-2">${banner}${bodyHtml}${gateRollupHtml || ''}</div>
            </div>
        </div>
        ${ts}
    </li>`;
}

// ===================================================================
// renderLogWindowFromJson — JSON-mode dispatcher
// ===================================================================

function renderLogWindowFromJson(jsonResp: RenderLogWindowInput): RenderedLogWindow | '' {
    if (!jsonResp || !Array.isArray(jsonResp.rolls)) return '';
    const rolls: Roll[] = jsonResp.rolls.filter(roll => roll.roll_type_1 !== 'paoe_entry_once' && roll.roll_type_1 !== 'area_no_targets'); // marker rows (log-render.ts)
    const campaignPermission = Number(jsonResp.campaign_permission || 0);

    const deathSessions = _ftDeathLogDetectSessions(rolls);
    const contestPeersByRid = _ftContestPeersByRid(rolls);
    const { gatesByRid, anchorIndexByRid, rollupEnabled } = _ftGateFailureCollect(rolls, campaignPermission);

    const groupedByNext: boolean[] = new Array(rolls.length);
    for (let i = 0; i < rolls.length; i++) {
        const current = rolls[i];
        const next = rolls[i + 1];
        groupedByNext[i] = !!current && !!next
            && String(current.roll_id || '') !== ''
            && String(current.roll_id || '') === String(next.roll_id || '');
    }

    const firstIndexByRid = new Map<string, number>();
    for (let i = 0; i < rolls.length; i++) {
        const current = rolls[i];
        if (!current) continue;
        const rid = String(current.roll_id || '');
        if (rid === '' || firstIndexByRid.has(rid)) continue;
        firstIndexByRid.set(rid, i);
    }

    const visibleIndicesByRid = new Map<string, number[]>();
    for (let i = 0; i < rolls.length; i++) {
        const r = rolls[i];
        if (!r) continue;
        const rid = String(r.roll_id || '');
        if (rid === '') continue;
        if (!_ftRollVisibleToViewer(r, campaignPermission)) continue;
        if (String(r.roll_type_1 || '') === 'post_effect_condition_failed' && rollupEnabled.has(rid)) continue;
        const t1 = String(r.roll_type_1 || '');
        if (t1 === 'trade_summary' || t1 === 'reading') continue;
        if (!visibleIndicesByRid.has(rid)) visibleIndicesByRid.set(rid, []);
        visibleIndicesByRid.get(rid)!.push(i);
    }
    const phase5GroupRids = new Set<string>();
    for (const [rid, indices] of visibleIndicesByRid) {
        if (indices.length < 2) continue;
        if (deathSessions.has(rid)) continue;
        if ((contestPeersByRid.get(rid) || []).length >= 2) continue;
        const hasTrade = indices.some(idx => String(rolls[idx]?.roll_type_1 || '') === 'trade_inventory');
        if (hasTrade) continue;
        phase5GroupRids.add(rid);
    }

    const contestingGroupRenderedRids = new Set<string>();
    const tradeRidsRendered = new Set<string>();
    const phase5CollapseIdByRid = new Map<string, string>();

    const collapseLabelTasks: CollapseLabelTask[] = [];

    let out = '';

    const _injectGateRollupIntoRowHtml = (rowHtml: string, rid: string, idx: number): string => {
        if (!rowHtml || !rollupEnabled.has(rid) || anchorIndexByRid.get(rid) !== idx) return rowHtml;
        const gr = renderGateFailureRollup(rid, gatesByRid.get(rid) || []);
        if (!gr.html) return rowHtml;
        if (gr.collapseId) collapseLabelTasks.push({
            collapseId: gr.collapseId,
            labelSelector: '.ft-log-gate-toggle-label',
            labelFor: (showing: boolean) => `${showing ? 'Hide ' : ''}gates / riders not met (${gr.count})`,
        });
        return rowHtml.replace(/<\/li>\s*$/, gr.html + '</li>');
    };

    for (let i = 0; i < rolls.length; i++) {
        const roll = rolls[i];
        if (!roll) continue;
        const rid = String(roll.roll_id || '');
        const t = String(roll.roll_type_1 || '');
        const suppressTs = !!groupedByNext[i];

        // Single source of truth for viewer visibility (secret roll / hidden-creature caster, lair
        // actions exempt). MUST match visibleIndicesByRid — otherwise a lair row that IS in a
        // visible group gets skipped here, and since it's visibleGroup[0] the whole Phase-5 group
        // then drops (every later member fails the `i !== visibleGroup[0]` guard).
        if (!_ftRollVisibleToViewer(roll, campaignPermission)) continue;

        if (t === 'post_effect_condition_failed' && rollupEnabled.has(rid)) {
            continue;
        }

        if (t === 'trade_summary' || t === 'reading') continue;

        // Contesting rollup unification (Phase 9b)
        if (rid !== ''
            && firstIndexByRid.get(rid) === i
            && contestPeersByRid.has(rid)
            && (contestPeersByRid.get(rid) || []).length >= 2
        ) {
            contestingGroupRenderedRids.add(rid);

            // Filter peers to what THIS viewer may see BEFORE building the VS summary — otherwise a
            // hidden non-lair contestant's result/skill/identity leaks (renderContestingSummaryBody
            // only guards roll_private===2, not a hidden-creature caster). If this drops below two
            // the summary comes back '' and the promoted-detail path below takes over.
            const peers = (contestPeersByRid.get(rid) || []).filter(p => _ftRollVisibleToViewer(p, campaignPermission));
            const summaryBody = renderContestingSummaryBody(peers, campaignPermission);

            const visIndices = visibleIndicesByRid.get(rid) || [];
            // Pre-render details; keep only rows that produced markup so the "details (N)"
            // count and the collapse both reflect what actually renders.
            const renderedDetails = visIndices.flatMap((idx: number) => {
                const r = rolls[idx];
                if (!r) return [] as string[];
                const html = renderSimpleLogRow(r, { inline: true, suppressTimestamp: true }) || '';
                return html ? [html] : [];
            });

            // Same structural bug as the Phase-5 group: the summary can render '' (e.g. a
            // secret contestant redacted for a non-GM viewer), which used to leave a card
            // with only a chevron + timestamp. Promote the first rendered detail to the
            // visible body in that case, and drop the whole rollup when neither a summary
            // nor any detail renders.
            let bodyHtml = summaryBody;
            let detailHtmls = renderedDetails;
            if (!bodyHtml) {
                bodyHtml = detailHtmls[0] || '';
                detailHtmls = detailHtmls.slice(1);
            }
            if (!bodyHtml) continue;

            const firstIdx = firstIndexByRid.get(rid) ?? 0;
            const firstRoll = rolls[firstIdx];
            if (!firstRoll) continue;
            const tsHtml = renderLogTimestamp(firstRoll, !!firstRoll.campaign_id);
            const n = detailHtmls.length;
            const ccId = 'mcr_' + (_hexOnly(rid) || Math.random().toString(16).slice(2, 18));

            // Always emit the collapse + toggle (toggle hidden when there are no rendered details) so
            // a 0->1 live-append has a nest target + counter to reveal, mirroring the death/Phase-5
            // pattern. Only wire the collapseLabelTask when the toggle starts visible — for the hidden
            // 0-count case the document-level flip delegate + append counter drive the label.
            out += `<li class="list-group-item mass-contest-rollup mb-1 pb-0 pt-0 border-bottom-0" data-ft-roll-id="${_esc(rid)}" style="position: relative;">
                ${bodyHtml}
                ${_chevronToggle({
                    collapseId: ccId,
                    count: n,
                    labelClass: 'mass-contest-toggle-label',
                    wrapClass: 'mass-contest-toggle-wrap',
                    hidden: n === 0,
                })}
                <div class="collapse mass-contest-detail" id="${_esc(ccId)}">
                    ${detailHtmls.join('')}
                </div>
                ${tsHtml}
            </li>`;

            if (n > 0) collapseLabelTasks.push({
                collapseId: ccId,
                labelSelector: '.mass-contest-toggle-label',
                labelFor: (showing: boolean) => `${showing ? 'Hide' : 'Show'} details (${n})`,
            });

            continue;
        }

        if (rid !== '' && contestingGroupRenderedRids.has(rid)) continue;

        // Trade-session
        if (t === 'trade_inventory' && rid !== '') {
            if (tradeRidsRendered.has(rid)) continue;
            tradeRidsRendered.add(rid);
            const rows = _ftTradeLogCollectRows(rolls, rid, campaignPermission);
            const first = rows[0];
            if (!first) continue;
            const nameFrom = String(first.roll_by_item_name || '') || 'Someone';
            const nameTo = String(first.roll_to_item_name || '') || 'Someone';
            const playerThumb = (first.roll_by_arr && typeof first.roll_by_arr === 'object') ? first.roll_by_arr : null;
            const merchantThumb = (first.roll_to_arr && typeof first.roll_to_arr === 'object') ? first.roll_to_arr : null;

            const anchorBody = `<div class="text-left trade-log-trade-group">
                <span class="visually-hidden">${_esc(nameFrom)} traded with ${_esc(nameTo)}</span>
                <div class="d-flex justify-content-center align-items-center gap-3">
                    ${playerThumb ? renderLogLineThumbnail(playerThumb, { width: 56, height: 56, thumbnail_link: 1 }) : ''}
                    <span class="d-inline-flex">${_icon('trade_inventory')}</span>
                    ${merchantThumb ? renderLogLineThumbnail(merchantThumb, { width: 56, height: 56, thumbnail_link: 1 }) : ''}
                </div>
            </div>`;
            let gateRollupHtml = '';
            if (rollupEnabled.has(rid) && anchorIndexByRid.get(rid) === i) {
                const gr = renderGateFailureRollup(rid, gatesByRid.get(rid) || []);
                gateRollupHtml = gr.html;
                if (gr.collapseId) collapseLabelTasks.push({
                    collapseId: gr.collapseId,
                    labelSelector: '.ft-log-gate-toggle-label',
                    labelFor: (showing: boolean) => `${showing ? 'Hide ' : ''}gates / riders not met (${gr.count})`,
                });
            }
            out += _wrapRow(roll, anchorBody, gateRollupHtml, { suppressTimestamp: suppressTs, suppressDataRollId: true });

            const n = rows.length;
            if (n > 0) {
                const tcId = 'tlc_' + (_hexOnly(rid) || Math.random().toString(16).slice(2, 18));
                const rowsHtml = rows.map(sub => {
                    let qty = parseInt(String(sub.roll_result || 1), 10);
                    if (qty < 1) qty = 1;
                    const qtyBadge = qty !== 1 ? `<span class="badge bg-dark border">${qty}</span>` : '';
                    const itemId = String(sub.roll_item_id || '');
                    const itemName = String(sub.roll_item_id_item_name || '');
                    const itemThumb = (sub.roll_item_arr && typeof sub.roll_item_arr === 'object') ? sub.roll_item_arr : null;
                    const toThumb   = (sub.roll_to_arr   && typeof sub.roll_to_arr   === 'object') ? sub.roll_to_arr   : null;
                    return `<li class="list-group-item">
                        <div class="small border-bottom pb-1 mb-2">
                            ${_icon('trade_inventory')}
                            ${qtyBadge}
                            <span role="button" class="link-primary" onclick="openItemInfo('${_esc(itemId)}');">${_esc(itemName)}</span>
                        </div>
                        <div class="d-flex align-items-center justify-content-center flex-wrap gap-1">
                            ${itemThumb ? renderLogLineThumbnail(itemThumb, { width: 52, height: 52, thumbnail_link: 1 }) : ''}
                            <div class="px-1">${_icon('trade_inventory')}</div>
                            ${toThumb   ? renderLogLineThumbnail(toThumb,   { width: 52, height: 52, thumbnail_link: 1 }) : ''}
                        </div>
                    </li>`;
                }).join('');

                out += `<li class="list-group-item trade-log-trade-rollup mb-1 pb-0 pt-0 border-bottom-0" data-ft-roll-id="${_esc(rid)}">
                    ${_chevronToggle({
                        collapseId: tcId,
                        count: n,
                        labelClass: 'trade-log-trade-toggle-label',
                        wrapClass: 'trade-log-trade-toggle-wrap',
                        labelText: 'Show details',
                    })}
                    <ul class="list-group list-group-flush collapse trade-log-trade-detail" id="${_esc(tcId)}">
                        ${rowsHtml}
                    </ul>
                </li>`;
            }
            continue;
        }

        // Death-session
        if (deathSessions.size > 0) {
            if (_ftDeathLogShouldSkipRow(roll, deathSessions)) continue;
            if (_ftDeathLogIsPrimaryRow(roll, deathSessions)) {
                const primaryRi = deathSessions.get(rid) ?? 0;
                const detailRolls = _ftDeathLogCollectDetailRows(rolls, rid, primaryRi, campaignPermission);

                let anchorHtml = renderSimpleLogRow(roll, {
                    suppressTimestamp: suppressTs,
                    suppressDataRollId: true,
                }) || '';
                if (rollupEnabled.has(rid) && anchorIndexByRid.get(rid) === i) {
                    const gr = renderGateFailureRollup(rid, gatesByRid.get(rid) || []);
                    if (gr.html) {
                        anchorHtml = anchorHtml.replace(/<\/li>\s*$/, gr.html + '</li>');
                        if (gr.collapseId) collapseLabelTasks.push({
                            collapseId: gr.collapseId,
                            labelSelector: '.ft-log-gate-toggle-label',
                            labelFor: (showing: boolean) => `${showing ? 'Hide ' : ''}gates / riders not met (${gr.count})`,
                        });
                    }
                }
                out += anchorHtml;

                const t_inner = String(roll.roll_type_1 || '');

                let transformThumb = '';
                if (t_inner === 'creature_transform') {
                    const pic = normalizePicUrl(roll.roll_to_pic_url);
                    if (pic) {
                        transformThumb = `<div class="text-center mb-2 mt-2 death-log-transform-into-thumb">
                            <img src="${_esc(pic)}" class="img-thumbnail mx-auto d-block" alt="" style="max-height: 120px; width: auto;"/>
                        </div>`;
                    }
                }

                // Count from what actually renders — a silent/unhandled detail must not inflate the
                // "Show details (N)" label or leave a blank row in the collapse. Omit the secondary
                // rollup entirely when there's neither a transform thumbnail nor a rendered detail
                // (keep the hidden chevron + empty collapse when a thumb is shown so the push-path
                // live-append still has a nest target).
                const detailHtmls = detailRolls.map(dr => renderSimpleLogRow(dr) || '').filter(Boolean);
                const n = detailHtmls.length;

                if (n > 0 || transformThumb) {
                    const dcId = 'dlc_' + (_hexOnly(rid) || Math.random().toString(16).slice(2, 18));
                    out += `<li class="list-group-item death-log-session-rollup mb-1 pb-0 pt-0 border-bottom-0" data-ft-roll-id="${_esc(rid)}">
                        ${transformThumb}
                        ${_chevronToggle({
                            collapseId: dcId,
                            count: n,
                            labelClass: 'death-log-toggle-label',
                            wrapClass: 'death-log-toggle-wrap',
                            hidden: (n === 0),
                        })}
                        <ul class="list-group list-group-flush collapse death-log-detail" id="${_esc(dcId)}">
                            ${detailHtmls.join('')}
                        </ul>
                    </li>`;
                }
                continue;
            }
        }

        // Default: simple row via JS handler
        const inPhase5 = phase5GroupRids.has(rid);
        const visibleGroup = inPhase5 ? visibleIndicesByRid.get(rid) : null;

        if (inPhase5 && visibleGroup) {
            // Render the WHOLE group at its first visible row: anchor + every later
            // member inside the collapse, then skip those members when the loop
            // reaches them. The previous streaming form (open the collapse at the
            // first member, close at the last) swept any interleaved rows from
            // OTHER roll_ids into the group's collapse <div> — broken nesting and
            // rows hidden from the main log whenever a group wasn't contiguous
            // (initiative bursts interleave janus-trigger rids with the request rid).
            if (i !== visibleGroup[0]) continue;

            // Pre-render every group member and keep only the rows that actually
            // produced markup. renderSimpleLogRow returns '' when a roll_type_1 has no
            // handler OR when a handler intentionally stays silent (e.g. a start-of-turn
            // temporary-vitality regen roll whose temp v wasn't exceeded). The old code
            // emitted this <li> + chevron + timestamp UNCONDITIONALLY, so a silent ANCHOR
            // left an empty card showing only a dropdown + timestamp (the reported bug).
            // Promote the first rendered row to the visible anchor, keep the rest in the
            // collapse, and drop the whole group when nothing renders.
            const renderedGroup = visibleGroup.flatMap(idx => {
                const groupRoll = rolls[idx];
                if (!groupRoll) return [] as { roll: Roll; html: string }[];
                const html = renderSimpleLogRow(groupRoll, { suppressTimestamp: true, inline: true }) || '';
                return html ? [{ roll: groupRoll, html }] : [];
            });
            const renderedAnchor = renderedGroup[0];
            const renderedDetails = renderedGroup.slice(1);

            let gateHtml = '';
            if (rollupEnabled.has(rid) && anchorIndexByRid.get(rid) === i) {
                const gr = renderGateFailureRollup(rid, gatesByRid.get(rid) || []);
                if (gr.html) {
                    gateHtml = gr.html;
                    if (gr.collapseId) collapseLabelTasks.push({
                        collapseId: gr.collapseId,
                        labelSelector: '.ft-log-gate-toggle-label',
                        labelFor: (showing: boolean) => `${showing ? 'Hide ' : ''}gates / riders not met (${gr.count})`,
                    });
                }
            }

            // Nothing renders and no gate rollup to show → don't emit an empty card.
            if (!renderedAnchor && !gateHtml) continue;

            out += `<li class="list-group-item ft-roll-group-collapsed mb-1 pb-0 pt-0 border-bottom-0" data-ft-roll-id="${_esc(rid)}" style="position: relative;">`;
            if (renderedAnchor) out += renderedAnchor.html;
            out += gateHtml;

            // Always emit the collapse + toggle (toggle hidden when there are no rendered details yet)
            // so a later live-append (_ftAppendToExistingGroup, 0->1) has a nest target + counter to
            // reveal — the death path uses the same hidden-toggle pattern. A hidden toggle shows
            // nothing, so this doesn't reintroduce a bare "0 more" chevron.
            const moreCount = renderedDetails.length;
            const phase5CollapseId = 'ftg_' + (_hexOnly(rid) || Math.random().toString(16).slice(2, 18));
            phase5CollapseIdByRid.set(rid, phase5CollapseId);
            out += `${_chevronToggle({
                collapseId: phase5CollapseId,
                count: moreCount,
                labelClass: 'ft-roll-group-toggle-label',
                wrapClass: 'ft-roll-group-toggle-wrap',
                hidden: moreCount === 0,
                labelText: `Show details (${moreCount} more)`,
            })}
            <div id="${_esc(phase5CollapseId)}" class="collapse ft-roll-group-detail">${renderedDetails.map(r => r.html).join('')}</div>`;

            const timestampRoll = renderedAnchor ? renderedAnchor.roll : roll;
            out += renderLogTimestamp(timestampRoll, !!timestampRoll.campaign_id);
            out += `</li>`;
            continue;
        }

        let rowHtml = renderSimpleLogRow(roll, { suppressTimestamp: suppressTs }) || '';
        rowHtml = _injectGateRollupIntoRowHtml(rowHtml, rid, i);
        out += rowHtml;
    }

    return { html: `<ul class="list-group list-group-flush">${out}</ul>`, collapseLabelTasks };
}

function activateCollapseLabelTasks(tasks: CollapseLabelTask[]): void {
    if (!Array.isArray(tasks)) return;
    for (const task of tasks) {
        _activateCollapseLabel(task.collapseId, task.labelSelector, task.labelFor);
    }
}

export {
    renderContestingSummary,
    renderDeathSessionBody,
    renderTradeSessionBody,
    renderGateFailureRollup,
    renderLogWindowFromJson,
    activateCollapseLabelTasks as activateLogCollapseTasks,
};
